import { clsx } from 'clsx';
import * as React from 'react';

import type { InputProps } from '../input';
import { Input } from '../input';
import styles from './text-field.module.scss';

type TextFieldProps = Omit<InputProps, 'classes'> & {
  label?: string;
  helperText?: string;
  error?: string;

  classes?: {
    input?: InputProps['classes'];
    root?: string;
    head?: string;
    footer?: string;
  };
};

const TextFieldRoot = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div ref={ref} className={clsx(styles.root, className)}>
    {children}
  </div>
));
TextFieldRoot.displayName = 'TextFieldRoot';

const TextFieldHead = React.forwardRef<
  HTMLLabelElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <label ref={ref} className={clsx(styles.head, className)}>
    {children}
  </label>
));
TextFieldHead.displayName = 'TextFieldHead';

const TextFieldControl = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div ref={ref} className={clsx(styles.control, className)}>
    {children}
  </div>
));
TextFieldControl.displayName = 'TextFieldControl';

const TextFieldFooter = React.forwardRef<
  HTMLDivElement,
  { className?: string; children: React.ReactNode }
>(({ className, children }, ref) => (
  <div ref={ref} className={clsx(styles.footer, className)}>
    {children}
  </div>
));
TextFieldFooter.displayName = 'TextFieldFooter';

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      error,
      leftAdornment,
      rightAdornment,
      className,
      classes,
      hasError,
      ...props
    },
    ref
  ) => {
    const showHelperText = hasError ? error : helperText;

    return (
      <TextFieldRoot className={clsx(classes?.root, className)}>
        {label && (
          <TextFieldHead className={classes?.head}>{label}</TextFieldHead>
        )}

        <Input
          ref={ref}
          classes={classes?.input}
          leftAdornment={leftAdornment}
          rightAdornment={rightAdornment}
          hasError={hasError}
          {...props}
        />

        {showHelperText && (
          <TextFieldFooter className={classes?.footer}>
            <span
              className={clsx(styles.helperText, {
                [styles.errorText]: hasError,
              })}
            >
              {error ?? helperText}
            </span>
          </TextFieldFooter>
        )}
      </TextFieldRoot>
    );
  }
);
TextField.displayName = 'TextField';

export {
  TextField,
  TextFieldControl,
  TextFieldFooter,
  TextFieldHead,
  TextFieldRoot,
};
export type { TextFieldProps };
